import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import Icon from '@mui/material/Icon';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import { useTheme as useMuiTheme, alpha } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import { uiBus } from 'shared/api/uiBus';
import { ConfirmDialog } from 'sectors/pilates/sessions/modal/parts/ConfirmDialog';

function normPhoneTR(v) {
  const s = (v || '').toString().replace(/[^\d]/g, '');
  if (s.length === 10 && s.startsWith('5')) return '0' + s;
  return s;
}

function fmtResetAt(v) {
  if (!v) return '';
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return '';
  }
}

function buildDialogItemsFromValidate(res) {
  const items = [];
  (res?.errors || []).forEach((e) => items.push({ title: 'Hata', message: e }));
  (res?.warnings || []).forEach((w) => items.push({ title: 'Uyarı', message: w }));

  if (res?.dailyLimit != null) {
    const line = `Günlük limit: ${res.dailyLimit} • Bugün: ${res.usedToday ?? 0} • Kalan: ${
      res.remainingToday ?? ''
    }`;
    items.push({ title: 'Kota', message: line });
  }
  if (res?.periodResetAt)
    items.push({ title: 'Sıfırlanma', message: fmtResetAt(res.periodResetAt) });
  if (res?.normalizedPhone)
    items.push({ title: 'Numara', message: `Normalize: ${res.normalizedPhone}` });
  if (res?.previewContent) items.push({ title: 'Önizleme', message: res.previewContent });

  return items;
}

export function SmsTestModal({ open, onClose, config, smsApi }) {
  const theme = useMuiTheme();
  const isDark = theme.palette.mode === 'dark';

  const [phone, setPhone] = React.useState('');
  const [extras, setExtras] = React.useState({});
  const [step, setStep] = React.useState('idle'); // idle | sending
  const [val, setVal] = React.useState(null);

  const [err, setErr] = React.useState('');
  const [ok, setOk] = React.useState('');

  // ✅ dialog state
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [confirmTone, setConfirmTone] = React.useState('info');
  const [confirmTitle, setConfirmTitle] = React.useState('Bilgi');
  const [confirmSubtitle, setConfirmSubtitle] = React.useState('');
  const [confirmItems, setConfirmItems] = React.useState([]);
  const [confirmConfirmText, setConfirmConfirmText] = React.useState('Tamam');
  const [confirmCancelText, setConfirmCancelText] = React.useState('Kapat');
  const [confirmShowConfirm, setConfirmShowConfirm] = React.useState(false);
  const [pendingSendPayload, setPendingSendPayload] = React.useState(null);

  // ✅ success dialog kapanınca modal kapatma
  const [closeParentOnDialogClose, setCloseParentOnDialogClose] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setPhone('');
    setExtras({});
    setStep('idle');
    setVal(null);
    setErr('');
    setOk('');

    setConfirmOpen(false);
    setConfirmTone('info');
    setConfirmTitle('Bilgi');
    setConfirmSubtitle('');
    setConfirmItems([]);
    setConfirmConfirmText('Tamam');
    setConfirmCancelText('Kapat');
    setConfirmShowConfirm(false);
    setPendingSendPayload(null);
    setCloseParentOnDialogClose(false);
  }, [open, config?.type]);

  const payload = React.useMemo(() => {
    return { type: config?.type, toPhone: normPhoneTR(phone), ...extras };
  }, [config?.type, phone, extras]);

  const toast = (type, message) => uiBus.emit?.('toast', { type, message });

  const canSendClick =
    !!payload?.type && (payload?.toPhone || '').length >= 10 && step !== 'sending';

  const closeSafe = () => {
    if (step === 'sending') return;
    onClose?.();
  };

  const openInfoDialog = React.useCallback((opts) => {
    setConfirmTitle(opts.title || 'Bilgi');
    setConfirmSubtitle(opts.subtitle || '');
    setConfirmTone(opts.tone || 'info');
    setConfirmItems(opts.items || []);
    setConfirmConfirmText(opts.confirmText || 'Tamam');
    setConfirmCancelText(opts.cancelText || 'Kapat');
    setConfirmShowConfirm(!!opts.showConfirm);

    setCloseParentOnDialogClose(!!opts.closeParentOnClose);

    setConfirmOpen(true);
  }, []);

  const doSendNow = React.useCallback(
    async (pl) => {
      setErr('');
      setOk('');
      setStep('sending');

      try {
        const res = await smsApi.sendTest(pl);
        setVal(res);

        if (res && res.ok === false) {
          const items = buildDialogItemsFromValidate(res);
          openInfoDialog({
            title: 'Gönderilemedi',
            subtitle: (res?.errors || [])[0] || 'Test SMS gönderilemedi.',
            tone: 'error',
            items,
            showConfirm: false,
            cancelText: 'Kapat',
            closeParentOnClose: false,
          });
          toast('error', (res?.errors || [])[0] || 'Gönderim başarısız.');
          setStep('idle');
          return;
        }

        openInfoDialog({
          title: 'Başarılı',
          subtitle: 'Test SMS kuyruğa alındı.',
          tone: 'success',
          items: res?.previewContent
            ? [{ title: 'Gönderilen içerik', message: res.previewContent }]
            : [],
          showConfirm: false,
          cancelText: 'Kapat',
          closeParentOnClose: true, // ✅ kapanınca modal da kapansın
        });

        toast('success', 'Test mesajı kuyruğa alındı.');
        setStep('idle');
      } catch (e) {
        const msg = e?.message || 'Test SMS gönderilemedi.';
        openInfoDialog({
          title: 'Hata',
          subtitle: msg,
          tone: 'error',
          items: [],
          showConfirm: false,
          cancelText: 'Kapat',
          closeParentOnClose: false,
        });
        toast('error', msg);
        setStep('idle');
      }
    },
    [smsApi, openInfoDialog]
  );

  const onClickSend = React.useCallback(async () => {
    if (!canSendClick) return;

    setErr('');
    setOk('');
    setVal(null);

    try {
      const res = await smsApi.validateTest(payload);
      setVal(res);

      if (!res?.ok) {
        const items = buildDialogItemsFromValidate(res);
        openInfoDialog({
          title: 'Gönderilemedi',
          subtitle: (res?.errors || [])[0] || 'Doğrulama başarısız.',
          tone: 'error',
          items,
          showConfirm: false,
          cancelText: 'Kapat',
          closeParentOnClose: false,
        });
        toast('error', (res?.errors || [])[0] || 'Doğrulama başarısız.');
        return;
      }

      const hasWarnings = (res?.warnings || []).length > 0;
      const lowQuota = (res?.remainingToday ?? 999) <= 1;

      if (hasWarnings || lowQuota) {
        const items = buildDialogItemsFromValidate(res);
        setPendingSendPayload(payload);

        openInfoDialog({
          title: 'Test Mesajı - Onay',
          subtitle: lowQuota
            ? 'Test mesajı için kalan hakkın az. Yine de göndermek istiyor musun?'
            : 'Bazı uyarılar var. Yine de göndermek istiyor musun?',
          tone: hasWarnings ? 'warning' : 'info',
          items,
          showConfirm: true,
          confirmText: 'Evet, gönder',
          cancelText: 'Vazgeç',
          closeParentOnClose: false, // ✅ vazgeç modalı kapatmasın
        });
        return;
      }

      await doSendNow(payload);
    } catch (e) {
      const msg = e?.message || 'Ön kontrol başarısız.';
      openInfoDialog({
        title: 'Hata',
        subtitle: msg,
        tone: 'error',
        items: [],
        showConfirm: false,
        cancelText: 'Kapat',
        closeParentOnClose: false,
      });
      toast('error', msg);
    }
  }, [canSendClick, smsApi, payload, openInfoDialog, doSendNow]);

  const onConfirmDialog = React.useCallback(() => {
    const pl = pendingSendPayload;
    setConfirmOpen(false);
    setPendingSendPayload(null);
    if (!pl) return;
    doSendNow(pl);
  }, [pendingSendPayload, doSendNow]);

  const onCancelDialog = React.useCallback(() => {
    setConfirmOpen(false);
    setPendingSendPayload(null);

    if (closeParentOnDialogClose) {
      setCloseParentOnDialogClose(false);
      onClose?.();
    }
  }, [closeParentOnDialogClose, onClose]);

  // ✅ ConfirmDialog ile aynı “soft” görünüm için
  const paperSx = {
    borderRadius: '22px',
    overflow: 'hidden',
    background: isDark ? alpha('#0b1220', 0.92) : alpha('#ffffff', 0.98),
    border: `1px solid ${alpha(theme.palette.info.main, 0.14)}`,
    boxShadow: '0 22px 60px rgba(0,0,0,.16), 0 10px 22px rgba(0,0,0,.12)',
    backgroundImage: 'none',
  };

  return (
    <Dialog
      open={open}
      onClose={closeSafe}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: paperSx }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0,0,0,.45)',
          backdropFilter: 'blur(6px)',
        },
      }}
    >
      {/* ✅ ConfirmDialog head tarzı */}
      <DialogTitle sx={{ pb: 1.2 }}>
        <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MDBox
            sx={{
              width: 40,
              height: 40,
              borderRadius: '14px',
              display: 'grid',
              placeItems: 'center',
              background: alpha(theme.palette.info.main, 0.12),
              border: `1px solid ${alpha(theme.palette.info.main, 0.22)}`,
              flex: '0 0 auto',
            }}
          >
            <Icon sx={{ color: theme.palette.info.main, fontSize: 20 }}>sms</Icon>
          </MDBox>

          <MDBox sx={{ minWidth: 0 }}>
            <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
              {config?.title || 'Test Mesajı'}
            </MDTypography>

            {config?.subtitle ? (
              <MDTypography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.35 }}>
                {config.subtitle}
              </MDTypography>
            ) : (
              <MDTypography variant="caption" sx={{ opacity: 0.72, display: 'block', mt: 0.35 }}>
                Test amaçlı SMS gönder.
              </MDTypography>
            )}
          </MDBox>
        </MDBox>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={1.1}>
          {/* istersen bunu kaldırabiliriz; asıl akış dialog */}
          {err ? (
            <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>
              {err}
            </Alert>
          ) : null}
          {ok ? <Alert severity="success">{ok}</Alert> : null}

          <TextField
            label="Test gönderilecek telefon"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
              setVal(null);
              setErr('');
              setOk('');
            }}
            placeholder="05xx xxx xx xx"
            size="small"
            fullWidth
          />

          {(config?.extraFields || []).map((f) => (
            <TextField
              key={f.key}
              label={f.label}
              value={extras[f.key] ?? ''}
              onChange={(e) => {
                setExtras((p) => ({ ...p, [f.key]: e.target.value }));
                setVal(null);
                setErr('');
                setOk('');
              }}
              placeholder={f.placeholder || ''}
              size="small"
              fullWidth
            />
          ))}

          <MDBox
            sx={{
              mt: 0.5,
              p: 1.2,
              borderRadius: '16px',
              border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
              background: alpha(theme.palette.info.main, isDark ? 0.07 : 0.05),
            }}
          >
            <MDTypography variant="caption" sx={{ opacity: 0.82, display: 'block' }}>
              Not: Bu işlem sadece test amaçlıdır.
            </MDTypography>
          </MDBox>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.2,
          py: 1.6,
          gap: 1,
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <MDButton
          variant="outlined"
          color="dark"
          onClick={closeSafe}
          disabled={step === 'sending'}
          sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 800 }}
          startIcon={<Icon>close</Icon>}
        >
          Kapat
        </MDButton>

        <MDButton
          variant="contained"
          color="info"
          onClick={onClickSend}
          disabled={!canSendClick || step === 'sending'}
          sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 900 }}
          startIcon={step === 'sending' ? <CircularProgress size={16} /> : <Icon>send</Icon>}
        >
          {step === 'sending' ? 'Gönderiliyor...' : 'Gönder'}
        </MDButton>

        {/* Tek dialog: hata/uyarı/başarı */}
        <ConfirmDialog
          open={confirmOpen}
          title={confirmTitle}
          subtitle={confirmSubtitle}
          items={confirmItems}
          tone={confirmTone}
          confirmText={confirmConfirmText}
          cancelText={confirmCancelText}
          busy={step === 'sending'}
          onCancel={onCancelDialog}
          onConfirm={confirmShowConfirm ? onConfirmDialog : onCancelDialog}
          tokens={{ isDark }}
          showConfirm={confirmShowConfirm} // ✅ ConfirmDialog tarafında destek olmalı
          maxWidth="sm"
          fullWidth
        />
      </DialogActions>
    </Dialog>
  );
}
